import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="portais--indice"
//
// Indice de secoes do artigo (rail da coluna direita). Tem duas
// responsabilidades e nenhuma alem disso: rolar suavemente ate a secao clicada
// e destacar a secao em que o leitor esta. A lista vem renderizada do servidor
// (componente Portais::IndiceComponent) — este controller NAO monta markup.
//
// O offset da navbar sticky nao e calculado aqui: os titulos tem
// `scroll-margin-top` no ck_content.css, entao o proprio browser posiciona o
// alvo do scrollIntoView. Para decidir qual secao esta "no topo" no destaque,
// a MESMA linha e lida de volta do browser (getComputedStyle) em
// linhaDestaque(), em vez de duplicar o calculo do CSS aqui — uma unica fonte
// de verdade para onde o scroll pousa e onde o destaque considera "chegou".
export default class extends Controller {
  static targets = ["item"]

  // Absorve arredondamento de subpixel: um titulo que pousa exatamente sobre
  // a linha de leitura (por exemplo apos scrollIntoView respeitar o
  // scroll-margin-top do CSS) precisa contar como alcancado, nao como "ainda
  // abaixo" por 1-2px de erro de medicao.
  static TOLERANCIA = 2

  // Debounce do resize: arrastar a borda da janela dispara o evento aos
  // centenas, e cada um reconstruiria o observer inteiro (ver criarObserver).
  static RESIZE_DEBOUNCE_MS = 100

  connect() {
    this.titulos = this.itemTargets
      .map(item => document.getElementById(item.dataset.id))
      .filter(Boolean)

    this.criarObserver()

    // rootMargin depende de window.innerHeight (ver criarObserver) e so e
    // lido na criacao do observer — por isso ele precisa ser reconstruido a
    // cada resize, nao apenas recalculado.
    this.onResize = () => {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => this.criarObserver(), this.constructor.RESIZE_DEBOUNCE_MS)
    }
    window.addEventListener("resize", this.onResize)

    this.destacar()
  }

  disconnect() {
    this.observer?.disconnect()
    window.removeEventListener("resize", this.onResize)
    clearTimeout(this.resizeTimeout)
  }

  navegar(event) {
    event.preventDefault()

    const id = event.currentTarget.dataset.id
    const titulo = document.getElementById(id)
    if (!titulo) return

    titulo.scrollIntoView({ behavior: "smooth", block: "start" })
    history.replaceState(null, "", `#${id}`)
    this.ativar(id)

    // preventDefault() acima descarta o comportamento nativo do <a href="#">
    // de mover o foco sequencial para o alvo. Sem isso, um usuario de teclado
    // que ativa a entrada continua no rail e o proximo Tab percorre o indice
    // em vez da secao para a qual navegou. tabindex="-1" torna o titulo
    // focavel programaticamente sem entrar na ordem de Tab; preventScroll
    // evita um segundo salto de scroll por cima do smooth scroll acima.
    titulo.setAttribute("tabindex", "-1")
    titulo.focus({ preventScroll: true })
  }

  destacar() {
    const linha = this.linhaDestaque()
    let atual = this.titulos[0]

    for (const titulo of this.titulos) {
      if (titulo.getBoundingClientRect().top > linha + this.constructor.TOLERANCIA) break
      atual = titulo
    }

    this.ativar(atual?.id)
  }

  // Fonte unica de verdade com o CSS: em vez de duplicar o
  // `calc(--layout-sticky-top + 1rem)` do scroll-margin-top aqui em JS (dois
  // lugares que podem se desalinhar), le o valor JA RESOLVIDO pelo browser
  // direto do primeiro titulo. E exatamente a linha onde o scrollIntoView
  // pousa o alvo, entao destacar()/criarObserver() concordam com ela por
  // construcao.
  //
  // Fallback para offsetTopo() (sem o +1rem do CSS) se não houver titulo
  // ainda montado ou o valor computado vier 0 — mantendo o comportamento
  // anterior nesse caso extremo, em vez de quebrar.
  linhaDestaque() {
    const titulo = this.titulos[0]
    if (titulo) {
      const valor = parseFloat(getComputedStyle(titulo).scrollMarginTop)
      if (valor) return valor
    }

    return this.offsetTopo() + 1
  }

  ativar(id) {
    this.itemTargets.forEach(item => {
      item.setAttribute("aria-current", item.dataset.id === id ? "true" : "false")
    })
  }

  // O observer serve de gatilho: sempre que um titulo cruza a linha de
  // destaque, recalculamos qual secao esta no topo por geometria real
  // (destacar() nao se importa qual titulo disparou a chamada). Dentro de
  // uma secao longa nada dispara — e nada precisa mudar mesmo.
  //
  // rootMargin encolhe a area observada a uma faixa de 2*TOLERANCIA sentada
  // exatamente sobre a mesma linha de leitura que destacar() usa
  // (linhaDestaque()) — mesma fonte, sem duplicar o offset. So encolher a
  // borda superior (como na primeira tentativa) nao basta: a borda inferior
  // ficava na base do viewport, entao ao rolar para cima um titulo so
  // cruzava essa borda centenas de pixels depois da linha real — o destaque
  // ficava defasado por quase uma tela inteira. Com as duas bordas
  // coincidindo na linha, qualquer titulo que cruzar a linha dispara a
  // transicao, subindo ou descendo.
  //
  // A margem inferior depende de window.innerHeight, entao muda com o
  // resize — e rootMargin so e lido na criacao do observer. Por isso o
  // observer e reconstruido (nao apenas recalculado) a cada resize, ver o
  // listener em connect()/disconnect().
  criarObserver() {
    this.observer?.disconnect()

    const linha = this.linhaDestaque()
    const tolerancia = this.constructor.TOLERANCIA
    const topo = Math.max(linha - tolerancia, 0)
    const abaixo = Math.max(window.innerHeight - linha - tolerancia, 0)

    this.observer = new IntersectionObserver(() => this.destacar(), {
      threshold: 0,
      rootMargin: `-${topo}px 0px -${abaixo}px 0px`
    })
    this.titulos.forEach(titulo => this.observer.observe(titulo))
  }

  // --layout-sticky-top e definido pelo layout (application.html.erb) e herdado
  // por este elemento. Vem em rem, entao converte para px pela raiz.
  offsetTopo() {
    const valor = getComputedStyle(this.element).getPropertyValue("--layout-sticky-top").trim()
    if (!valor) return 0
    if (valor.endsWith("rem")) {
      return parseFloat(valor) * parseFloat(getComputedStyle(document.documentElement).fontSize)
    }
    return parseFloat(valor) || 0
  }
}
